/**
 * CM > IPS > 침입탐지/보안 프로파일 > 프로파일 정책
 *
 * @since 2021-08-09
 * @author wj_tams_dev04
 */
import {i18n} from '~/plugins/i18n'
import {SystemApiPrefix as smApiPrefix, CmApiPrefix as cmApiPrefix} from '~/utils/restApi'

class ProfileData {
  constructor (obj, modelType = 'ips') {
    this.keyList = []
    this.orgKeyList = []
    if (_.has(obj, 'ngips_prf_id')) this.modelType = 'ips'
    else if (_.has(obj, 'ads_ips_prf_id')) this.modelType = 'ads'
    else this.modelType = modelType

    this.prf_id = this.getValue(obj, ['ngips_prf_id', 'ads_ips_prf_id'], 'prf_id')
    this.prf_name = this.getValue(obj, ['ngips_prf_name', 'ads_ips_prf_name'], 'prf_name')
    this.prf_desc = this.getValue(obj, ['ngips_prf_desc', 'ads_ips_prf_desc'], 'prf_desc')
    this.prf_use_yn = this.getValue(obj, ['ngips_prf_use_yn', 'ads_ips_prf_use_yn'], 'prf_use_yn')
    this.prf_direct = this.getValue(obj, ['ngips_prf_direct', 'ads_ips_prf_direct'], 'prf_direct')
    this.base_signtr_tmplt_id = this.getValue(obj, ['ngips_prf_base_signtr_tmplt_id', 'base_signtr_tmplt_id'], 'base_signtr_tmplt_id')
    this.prtect_rqst_level_id = this.getValue(obj, ['ngips_prf_prtect_rqst_level_id', 'prtect_rqst_level_id'], 'prtect_rqst_level_id')
    this.pd_use_yn = this.getValue(obj, ['pd_use_yn', 'pd_use_yn'], 'pd_use_yn')
  }
  getValue (obj, arr, key) {
    this.keyList.push(key)
    this.orgKeyList.push(this.modelType === 'ips' ? arr[0] : arr[1])
    return _.cloneDeep(_.get(_.values(_.pick(obj, [...arr, key])), 0, void 0))
  }
  get () {
    const ret = {}
    _.forEach(this.keyList, key => {
      ret[key] = this[key]
    })
    return ret
  }
  getApiParam () {
    const ret = {}
    _.forEach(this.keyList, (key, idx) => {
      const orgKey = this.orgKeyList[idx]
      ret[orgKey] = this[key]
    })
    return ret
  }
}

/**
 * 침입탐지방지 정책 페이지의 기본 Tree Node를 초기화한다.
 */
function setDefaultProfileToNode () {
  return [
    {
      key: 'profilePolicy',
      label: i18n.t('op.detectionPolicyProfile_profilePolicy'),
      children: []
    }
  ]
}

/**
 * 프로파일 Node의 자식 노드를 설정하여 리턴한다.
 * ips에서는 isIps, isDdos, isLearning 조건 정책에 따라 자식노드가 달라짐. 추후 정책 확인 필요.
 *  - direction: 0: 양방향, 1: IN, 2: OUT
 */
function setProfileChildrenToNode (id, direction, modelType = 'ips') {
  const nodeDict = {
    signature: { // 시그니처
      key: `${id}_signature`,
      label: i18n.t('op.detectionPolicyProfile_signature'),
      children: [
        {
          key: `${id}_signatureDistributionRule`,
          label: i18n.t('op.detectionPolicyProfile_distributionRule') // 배포룰
        },
        {
          key: `${id}_signatureUserDefine`,
          label: i18n.t('op.detectionPolicyProfile_userDefine') // 사용자 정의
        }
      ]
    },
    serviceAttackDefense: { // 서비스 공격 방어
      key: `${id}_serviceAttackDefense`,
      label: i18n.t('op.detectionPolicyProfile_serviceAttackDefense')
    },
    learningDefense: { // 학습 방어
      key: `${id}_learningDefense`,
      label: i18n.t('op.detectionPolicyProfile_learningDefense'),
      children: [
        {
          key: `${id}_learningDefensePattern`,
          label: i18n.t('op.detectionPolicyProfile_patternLearningDefense') // 패턴 학습 방어
        },
        {
          key: `${id}_learningDefenseTraffic`,
          label: i18n.t('op.detectionPolicyProfile_trafficLearningDefense') // 트래픽 학습 방어
        }
      ]
    },
    sourceSessionControl: { // 발신 기반 세션 제어
      key: `${id}_sourceSessionControl`,
      label: i18n.t('op.detectionPolicyProfile_sourceSessionControl')
    },
    authDefenseHeader: {
      key: `${id}_authDefenseHeader`,
      label: i18n.t('op.protectionPolicyProfile_header'), // 인증 기반 방어
      children: [
        {
          key: `${id}_authDefenseTcp`,
          label: i18n.t('op.protectionPolicyProfile_tcp') // TCP 인증
        },
        {
          key: `${id}_authDefenseUdp`,
          label: i18n.t('op.protectionPolicyProfile_udp') // UDP 인증
        },
        {
          key: `${id}_authDefenseDns`,
          label: i18n.t('op.protectionPolicyProfile_dns') // DNS 인증
        },
        {
          key: `${id}_authDefenseHttp`,
          label: i18n.t('op.protectionPolicyProfile_http') // HTTP 인증
        }
      ]
    }
  }
  if (modelType === 'ads') {
    // 아웃바운드 정책 처리
    if (direction === 2) return [nodeDict.serviceAttackDefense, nodeDict.signature]
    return [nodeDict.serviceAttackDefense, nodeDict.learningDefense, nodeDict.authDefenseHeader, nodeDict.signature]
  }
  // ips
  if (direction === 2) return [nodeDict.signature, nodeDict.serviceAttackDefense]
  return [nodeDict.signature, nodeDict.serviceAttackDefense, nodeDict.learningDefense, nodeDict.sourceSessionControl]
}

export const state = () => ({
  defaultId: 33,
  modelType: 'ips', // ads, ips
  profileList: [],
  orgProfileList: [],
  profileNodes: [],
  signatureList: [],
  protectReqLevelList: []
})

export const mutations = {
  initStore (state) {
    state.profileList = []
    state.orgProfileList = []
    state.profileNodes = []
    state.signatureList = []
    state.protectReqLevelList = []
  },
  setModelType (state, modelType) {
    state.modelType = modelType
  },
  /**
   * 침입탐지/보안 프로파일을 셋팅한다.
   */
  setProfileList (state, profileList) {
    state.profileList = _.cloneDeep(profileList)
  },
  /**
   * 침입탐지/보안 프로파일을 수정한다. 단일 수정 / 삭제 시 호출한다.
   */
  setProfile (state, profile) {
    // 침입탐지 프로파일에 적용
    const targetIdx = state.profileList.findIndex(({prf_id: id}) => id === profile.prf_id)
    if (targetIdx > -1) {
      const copy = _.cloneDeep(state.profileList)
      copy[targetIdx] = profile
      state.profileList = copy
    }
  },
  /**
   * 침입탐지정책 프로파일을 수정한다. 멀티셀렉트 수정 / 삭제 시 호출한다.
   */
  setProfiles (state, profiles) {
    const copy = _.cloneDeep(state.profileList)
    _.forEach(profiles, (profile) => {
      const targetIdx = state.profileList.findIndex(({prf_id: id}) => id === profile.prf_id)
      if (targetIdx > -1) {
        copy[targetIdx] = profile
      }
    })
    state.profileList = copy
  },
  /**
   * 프로파일을 추가하면 좌측 트리 노드에 추가한다.
   */
  addProfileNode (state, {id, name, desc, direct}) {
    const newNode = {
      name,
      desc,
      key: `${id}_profile`,
      img: require(`assets/img/icon/ico_profile_${direct === 0 ? 'both' : direct === 1 ? 'in' : 'out'}.png`),
      label: name,
      header: 'profile',
      children: setProfileChildrenToNode(id, direct, state.modelType),
      prf_id: id,
      direct: direct
    }

    state.profileNodes[0].children.push(newNode)
    // 아이디 오름차순으로 Tree 정렬 Table 정렬과 순서 맞추기 위한 코드
    state.profileNodes[0].children.sort((a, b) => a.prf_id - b.prf_id)
  },
  /**
   * 프로파일을 수정하면 좌측 트리노드에 수정 사항을 반영한다.
   */
  modProfileNode (state, profiles = {}) {
    const idx = state.profileNodes[0].children.findIndex(({prf_id: originId}) => originId === profiles.prf_id)

    if (idx > -1) {
      state.profileNodes[0].children[idx].name = profiles.prf_name
      state.profileNodes[0].children[idx].desc = profiles.prf_desc
      state.profileNodes[0].children[idx].label = profiles.prf_name
    }
  },
  /**
   * 프로파일을 삭제하면 좌측 트리 노드에서 대상 노드를 제거한다.
   */
  delProfileNode (state, idList = []) {
    state.profileNodes[0].children = state.profileNodes[0].children.filter(({ngips_prf_id: id}) => idList.indexOf(id) < 0)
  },
  /**
   * 침입탐지정책 페이지 전체를 초기화한다. 최초 Mounted 발생 시 사용한다.
   */
  setDetectionPolicyNodes (state, profiles = []) {
    // 0. 프로파일 목록 추가
    state.profileList = _.map(profiles, o => new ProfileData(o))
    state.orgProfileList = _.cloneDeep(_.map(profiles, o => new ProfileData(o).get()))

    // 1. 프로파일 Tree 포맷 변경
    const profileNodes = []
    const copyProfiles = _.cloneDeep(_.map(profiles, o => new ProfileData(o).get()))
    for (const profile of copyProfiles) {
      const id = profile.prf_id
      const direct = profile.prf_direct
      profileNodes.push({
        key: `${id}_profile`,
        img: require(`assets/img/icon/ico_profile_${direct === 0 ? 'both' : direct === 1 ? 'in' : 'out'}.png`),
        name: profile.prf_name,
        desc: profile.prf_desc,
        label: profile.prf_name,
        header: 'profile',
        direct: direct,
        children: setProfileChildrenToNode(id, direct, state.modelType),
        prf_id: id
      })
    }

    state.profileNodes[0].children = profileNodes
  },
  /**
   * 침입탐지방지 정책의 기본 트리노드를 생성하여 초기화한다. 반드시 setDetectionPolicyNodes 보다 선행으로 실행되어야한다.
   */
  initDetectionPolicyNodes (state) {
    state.profileNodes = setDefaultProfileToNode()
  },
  /**
   * 프로파일 정책 목록을 모두 초기화한다.
   */
  initDetectionPolicyList (state, payload) {
    state.profileList = _.cloneDeep(_.map(payload, o => new ProfileData(o)))
    state.orgProfileList = _.cloneDeep(_.map(payload, o => new ProfileData(o)))
  },
  /**
   * 프로파일 정책 목록만 설정한다.
   */
  setDetectionPolicyList (state, payload) {
    state.profileList = _.cloneDeep(payload)
  },
  /**
   * 원본 프로파일 정책 목록만 설정한다.
   */
  setOrgDetectionPolicyList (state, payload) {
    // 원본 침입탐지 프로파일에 수정
    state.orgProfileList = _.map(state.orgProfileList, (item) => {
      return (item.prf_id === payload.prf_id) ? payload : item
    })
  },
  /**
   * 원본 프로파일 정책에 정책 추가한다.
   */
  putOrgDetectionPolicyList (state, payload) {
    state.orgProfileList.push(_.cloneDeep(payload))
  },
  /**
   * 시그니처 목록을 초기화한다.
   */
  initSignatureTemplates (state, payload) {
    state.signatureList = payload
  },
  /**
   * 보호 요구 레벨 목록을 초기화한다.
   */
  initProtectReqLevelList (state, payload) {
    state.protectReqLevelList = payload
  }
}

export const actions = {
  /**
   * 침입탐지정책 목록을 초기화한다.
   * 옵션: 1) all > 노드를 포함한 모든 프로파일을 초기화한다.
   */
  async getDetectionProfileList ({state, commit}, {all = false, loading = true, isNotAlert = false}) {
    let res = {code: 'ok'}

    try {
      const url = (state.modelType === 'ips') ? `${smApiPrefix}/intrusion-detections` : `${cmApiPrefix}/adsprf/protection-profile`
      const {code, result} = await this.$axios.$get(url, {loading, isNotAlert})

      if (code === 'ok') {
        if (all) {
          commit('setDetectionPolicyNodes', result)
        } else {
          commit('initDetectionPolicyList', result)
        }
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },
  /**
   * 침입탐지정책 프로파일을 추가한다.
   */
  async addDetectionProfile ({state, dispatch, commit}, profile) {
    let res = {code: 'ok'}

    try {
      const param = new ProfileData(profile, state.modelType).getApiParam()
      const url = (state.modelType === 'ips') ? `${smApiPrefix}/intrusion-detections` : `${cmApiPrefix}/adsprf/protection-profile`
      const {code, result} = await this.$axios.$post(url, param)

      if (code === 'ok') {
        // 추가된 침입탐지 프로파일
        const profiles = _.map(result, o => new ProfileData(o))
        const addProfile = _.head(_.filter(profiles, {'prf_name': profile.prf_name}))
        // 프로파일 노드 추가
        commit('addProfileNode', {
          id: addProfile.prf_id,
          name: profile.prf_name,
          desc: profile.prf_desc,
          direct: profile.prf_direct
        })

        // 침입탐지 프로파일 전체 적용
        commit('setDetectionPolicyList', profiles)
        // 원본 침입탐지 프로파일에 추가
        commit('putOrgDetectionPolicyList', addProfile)
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },
  /**
   * 침입탐지정책 프로파일을 수정한다.
   */
  async modDetectionProfile ({state, dispatch, commit}, {params = [], loading = false, successAlert = false}) {
    let res = {code: 'ok'}

    try {
      const items = _.map(params, o => {
        const profile = new ProfileData(o, state.modelType)
        return profile.getApiParam()
      })
      const url = (state.modelType === 'ips') ? `${smApiPrefix}/intrusion-detections` : `${cmApiPrefix}/adsprf/protection-profile`
      const {code, result} = await this.$axios.$put(url, {items}, {loading, successAlert})

      if (code === 'ok') {
        // 하나만 수정되는 경우만 업데이트
        if (params.length === 1) {
          commit('setProfile', new ProfileData(result))
          commit('modProfileNode', new ProfileData(result))
          commit('setOrgDetectionPolicyList', new ProfileData(result))
        }
      } else if (code === 'pok') {
        res = {code: 'pok', reason: result.errors}
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },
  /**
   * 침입탐지정책 프로파일을 삭제한다.
   */
  async delDetectionProfile ({state, dispatch, commit}, {params, loading = true, successAlert = true}) {
    let res = {code: 'ok'}

    try {
      const url = (state.modelType === 'ips') ? `${smApiPrefix}/intrusion-detections` : `${cmApiPrefix}/adsprf/protection-profile`
      const batchParams = (state.modelType === 'ips') ? {ngips_prf_list: params} : {ads_ips_prf_id_list: params}
      const {code, result} = await this.$axios.$delete(url,
        {data: batchParams, loading, successAlert})
      if (code !== 'ok') {
        res = {code: 'pok', reason: result.errors}
      }
    } catch (e) {
      res = {code: 'nok', reason: [e]}
    }
    return res
  },
  /**
   * 보호 요구 레벨 리스트를 불러온다.
   */
  async getProtectReqLevel ({commit}) {
    let res = {code: 'ok'}

    try {
      const {code, result} = await this.$axios.$get(`${smApiPrefix}/protection-required-levels`,
        {loading: false})

      if (code === 'ok') {
        commit('initProtectReqLevelList', result)
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },
  /**
   * 기본 시그니처 템플릿 리스트를 불러온다
   */
  async getSignatureTemplates ({commit}) {
    let res = {code: 'ok'}

    try {
      const {code, result} = await this.$axios.$get(`${smApiPrefix}/basic-signature-templates`,
        {loading: false})

      if (code === 'ok') {
        commit('initSignatureTemplates', result)
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },
  /**
   * 전달된 IPS 프로파일을 참조하고 있는 기능을 조회한다.
   */
  async getIpsProfileReference ({state, commit}, id) {
    let res = {code: 'ok'}

    try {
      let url = ''
      if (state.modelType === 'ips') url = `${smApiPrefix}/intrusion-detections/${id}/policy-domain`
      else if (state.modelType === 'ads') url = `${cmApiPrefix}/adsprf/protection-profile/${id}/policy-domain`
      const {code, result} = await this.$axios.$get(url)
      if (code === 'ok') {
        res.result = result
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },
  /**
   * 변경된 침입탐지정책 데이터를 적용/취소한다.
   */
  async finalCommand ({state, dispatch}, type) {
    let res = {code: 'ok'}

    try {
      const url = (state.modelType === 'ips') ? `${smApiPrefix}/intrusion-detections/${type}` : `${cmApiPrefix}/adsprf/protection-profile/${type}`
      const {code, result} = await this.$axios.$put(url, null, {loading: false, successAlert: false})

      if (code !== 'ok') {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  }
}
